const TOKEN_KEY = 'metis-drive-token';
const SCOPES = 'https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/drive.file';

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';
const FOLDER_ID = import.meta.env.VITE_TICKET_DRIVE_FOLDER_ID ?? '';

let gisReady = null;

function loadGisScript() {
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  if (gisReady) return gisReady;

  gisReady = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Impossibile caricare Google Identity Services'));
    document.head.appendChild(script);
  });
  return gisReady;
}

function saveToken(tokenResponse) {
  const payload = {
    accessToken: tokenResponse.access_token,
    expiresAt: Date.now() + Number(tokenResponse.expires_in ?? 3600) * 1000,
  };
  localStorage.setItem(TOKEN_KEY, JSON.stringify(payload));
  return payload.accessToken;
}

export function isDriveConfigured() {
  return Boolean(CLIENT_ID && FOLDER_ID);
}

export function isDriveConnected() {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed.expiresAt > Date.now() + 30_000;
  } catch {
    return false;
  }
}

export function disconnectDrive() {
  localStorage.removeItem(TOKEN_KEY);
}

export async function getDriveAccessToken({ interactive = false } = {}) {
  if (!CLIENT_ID) throw new Error('Configura VITE_GOOGLE_CLIENT_ID');

  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.expiresAt > Date.now() + 30_000) return parsed.accessToken;
    }
  } catch {
    /* ignore */
  }

  if (!interactive) throw new Error('Drive non connesso');

  await loadGisScript();

  return new Promise((resolve, reject) => {
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: SCOPES,
      callback: (response) => {
        if (response.error) {
          reject(new Error(response.error_description ?? response.error));
          return;
        }
        resolve(saveToken(response));
      },
    });
    client.requestAccessToken({ prompt: '' });
  });
}

export async function connectGoogleDrive() {
  return getDriveAccessToken({ interactive: true });
}

export async function listTicketFiles(folderId = FOLDER_ID) {
  const token = await getDriveAccessToken({ interactive: true });
  const q = encodeURIComponent(`'${folderId}' in parents and trashed=false`);
  const fields = encodeURIComponent('files(id,name,mimeType,modifiedTime,thumbnailLink,webViewLink,iconLink)');
  const url = `https://www.googleapis.com/drive/v3/files?q=${q}&fields=${fields}&orderBy=modifiedTime%20desc&pageSize=100`;

  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message ?? `Drive API error ${res.status}`);
  }
  const data = await res.json();
  return data.files ?? [];
}

export async function uploadTicketFile(file, folderId = FOLDER_ID) {
  const token = await getDriveAccessToken({ interactive: true });
  const metadata = { name: file.name, parents: [folderId] };
  const body = new FormData();
  body.append(
    'metadata',
    new Blob([JSON.stringify(metadata)], { type: 'application/json' }),
  );
  body.append('file', file);

  const res = await fetch(
    'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,mimeType,webViewLink,modifiedTime',
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body,
    },
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message ?? `Upload fallito (${res.status})`);
  }
  return res.json();
}

export function getDriveFolderUrl() {
  if (!FOLDER_ID) return null;
  return `https://drive.google.com/drive/folders/${FOLDER_ID}`;
}
